import { Close, InfoOutlined, Upload } from '@mui/icons-material';
import { IconButton } from '@mui/material';

import { Button, Spinner } from '@ifs-gcp/components';

import { ACTIONS } from '@/constants/actions';
import { LANGUAGE_FLAGS } from '@/constants/languages';
import { useAvailableActions } from '@/hooks/useAvailableActions';
import { useErrorHandling } from '@/hooks/useErrorHandling';
import { useScrollIntoView } from '@/hooks/useScrollIntoView';
import { useGetAuditCaseDocumentDownloadUrl } from '@/queries/get-audit-case-document-download-url';
import { useAuditStore } from '@/store/auditStore';
import type { UploadedDocument } from '@/types/documentUpload';

import styles from './documents.module.scss';

interface DocumentsTableProps {
  id: string;
  typeName: string;
  isRequired: boolean;
  isUploadable: boolean;
  isUploading: boolean;
  auditCaseUuid: string;
  documents: UploadedDocument[];
  translations: Record<string, string>;
  onUploadClick: () => void;
  onDeleteClick: (doc: UploadedDocument) => void;
}

export const DocumentsTable = ({
  id,
  typeName,
  isRequired,
  isUploadable,
  isUploading,
  auditCaseUuid,
  documents,
  translations,
  onUploadClick,
  onDeleteClick,
}: DocumentsTableProps) => {
  const { availableActions } = useAvailableActions();
  const hasUpdatePermissions = availableActions.has(ACTIONS.AUDIT_CASE_UPDATE);
  const isActionPending = useAuditStore((state) => state.isActionPending);
  const [scrollRef] = useScrollIntoView(id);

  const handleErrors = useErrorHandling();
  const { mutateAsync: fetchDownloadUrl, isPending: isDownloading } =
    useGetAuditCaseDocumentDownloadUrl();

  const handleDownloadClick = async (documentUuid: string) => {
    try {
      const { url } = await fetchDownloadUrl({ auditCaseUuid, documentUuid });

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleErrors(error);
    }
  };

  return (
    <div ref={scrollRef}>
      <div className={styles.sectionHeader}>
        <div className={styles.titleGroup}>
          <span className={styles.typeName}>{typeName}</span>
          {isRequired && (
            <span className={styles.missingBadge}>{translations.documentMissing}</span>
          )}
        </div>
        {hasUpdatePermissions && (
          <Button
            variant="text"
            color="primary"
            startIcon={isUploading ? <Spinner size={16} /> : <Upload />}
            disabled={!isUploadable || isActionPending}
            onClick={onUploadClick}
            className={styles.addFileButton}
          >
            {translations.addFile}
          </Button>
        )}
      </div>

      <table className={styles.fileTable}>
        <tbody>
          {documents.length === 0 ? (
            <tr>
              <td colSpan={3} className={styles.emptyState}>
                <InfoOutlined className={styles.infoIcon} fontSize="small" />
                <span>{translations.noFileUploaded}</span>
              </td>
            </tr>
          ) : (
            documents.map((doc) => (
              <tr key={doc.uuid} className={styles.fileRow}>
                <td className={styles.fileName}>
                  <button
                    className={styles.fileNameLink}
                    disabled={isDownloading}
                    onClick={() => handleDownloadClick(doc.uuid)}
                  >
                    {doc.displayName ?? doc.fileName}
                  </button>
                </td>
                <td className={styles.languageCell}>
                  {doc.language && (
                    <img
                      className={styles.languageFlag}
                      src={LANGUAGE_FLAGS[doc.language]}
                      alt={doc.language}
                    />
                  )}
                </td>
                {hasUpdatePermissions && (
                  <td className={styles.deleteCell}>
                    <IconButton
                      size="small"
                      className={styles.deleteButton}
                      aria-label="delete"
                      disabled={isActionPending}
                      onClick={() => onDeleteClick(doc)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
